import React, { useState, useContext, useEffect } from "react";
import { View, Text, TextInput, Pressable, StyleSheet } from "react-native";
import AsyncStorage from "@react-native-async-storage/async-storage";

import { appColors, cardShadow } from "../constants/colors";
import { CalculationsContext } from "../store/calculationsCtx";
import { sanitizeNumericInput } from "../utils/helpers";
import CustomDropdown from "./UI/CustomDropdown";

function AdjustmentRow({ index, add, onUpdate, onAdd, onDelete, months, length }) {
  const onMonthChange = m => onUpdate(index, "term", m);

  return (
    <>
      <View style={styles.rowContainer}>
        <View style={styles.pickerWrapper}>
          {index === 0 && <Text style={styles.inputLabel}>Month</Text>}
          <View style={styles.pickerOutline}>
            <CustomDropdown list={months} onMonthChange={onMonthChange} value={add.term} />
          </View>
        </View>

        <View style={styles.valueWrapper}>
          {index === 0 && <Text style={styles.inputLabel}>Extra amount</Text>}
          <TextInput
            style={styles.textInput}
            keyboardType="numeric"
            value={`${add.amount}`}
            onChangeText={e => onUpdate(index, "amount", e)}
          />
        </View>

        {index !== 0 && index === length - 1 && (
          <Pressable onPress={() => onDelete(index)} style={styles.deleteBtn}>
            <Text style={styles.deleteText}>✕</Text>
          </Pressable>
        )}
        {index !== 0 && index !== length - 1 && <View style={styles.deleteBtn} />}
        {index === 0 && length === 1 && (
          <Pressable onPress={() => onDelete(index)} style={styles.deleteBtn}>
            <Text style={styles.deleteText}>✕</Text>
          </Pressable>
        )}
        {index === 0 && length > 1 && <View style={styles.deleteBtn} />}
      </View>
    </>
  );
}

function AdjustmentRowInt({ index, int, onUpdate, onAdd, onDelete, months, length }) {
  const onMonthChange = m => onUpdate(index, "term", m);

  return (
    <>
      <View style={styles.rowContainer}>
        <View style={styles.pickerWrapper}>
          {index === 0 && <Text style={styles.inputLabel}>From month</Text>}
          <View style={styles.pickerOutline}>
            <CustomDropdown list={months} onMonthChange={onMonthChange} value={int.term} />
          </View>
        </View>

        <View style={styles.valueWrapper}>
          {index === 0 && <Text style={styles.inputLabel}>Rate (%)</Text>}
          <TextInput
            style={styles.textInput}
            keyboardType="numeric"
            value={`${int.rate}`}
            onChangeText={e => onUpdate(index, "rate", e)}
          />
        </View>

        {index !== 0 && index === length - 1 && (
          <Pressable onPress={() => onDelete(index)} style={styles.deleteBtn}>
            <Text style={styles.deleteText}>✕</Text>
          </Pressable>
        )}
        {index !== 0 && index !== length - 1 && <View style={styles.deleteBtn} />}
        {index === 0 && length === 1 && (
          <Pressable onPress={() => onDelete(index)} style={styles.deleteBtn}>
            <Text style={styles.deleteText}>✕</Text>
          </Pressable>
        )}
        {index === 0 && length > 1 && <View style={styles.deleteBtn} />}
      </View>
    </>
  );
}

// First-time spotlight badge — shown until the person has used the feature
// at least once, then it stops appearing (tracked via AsyncStorage).
// const BADGE_DISMISS_KEY = "advanced-settings-badge-seen";

const AdvancedSettings = () => {
  const [activeTab, setActiveTab] = useState("adds"); // "adds" | "rates"
  // const [showBadge, setShowBadge] = useState(false);

  const calculationsCtx = useContext(CalculationsContext);
  const { inputs, setInputs, setCalculators, calculators, results, results2, currency } = calculationsCtx;
  const adds = inputs.extraPayments || [];
  const interestChanges = inputs.rateChanges || [];
  const term = inputs.term;
  const hasAdjustments = adds.length > 0 || interestChanges.length > 0;

  const months = Array.from({ length: inputs.term }, (_, i) => i + 1);
  const months2 = Array.from({ length: inputs.term }, (_, i) => i + 2);

  // Check whether to show the "feature spotlight" badge — only for people
  // who haven't used Advanced Settings before.
  // useEffect(() => {
  //   AsyncStorage.getItem(BADGE_DISMISS_KEY).then(seen => {
  //     if (!seen) setShowBadge(true);
  //   });
  // }, []);

  // useEffect(() => {
  //   if (hasAdjustments && showBadge) {
  //     AsyncStorage.setItem(BADGE_DISMISS_KEY, "1");
  //     setShowBadge(false);
  //   }
  // }, [hasAdjustments]);

  useEffect(() => {
    const saveToStorage = async () => {
      const newArray = calculators.map(item => (item.active ? { ...item, inputs } : item));
      await AsyncStorage.setItem("calculators", JSON.stringify(newArray));
      setCalculators(newArray);
    };

    if (hasAdjustments) saveToStorage();
  }, [inputs]);

  const onUpdate = (index, field, value0) => {
    const value = sanitizeNumericInput(String(value0));
    setInputs(prev => {
      const newExtra = prev.extraPayments.map((item, i) => (i === index ? { ...item, [field]: value } : item));
      return { ...prev, extraPayments: newExtra };
    });
  };

  const onAdd = () => {
    setInputs(prev => {
      const current = prev.extraPayments || [];
      const lastTerm = current.length > 0 ? current[current.length - 1].term : 0;
      const newMonths = months.filter(m => m > lastTerm);
      if (newMonths.length === 0) return prev;
      return {
        ...prev,
        extraPayments: [...current, { term: newMonths[0], amount: "0", months: newMonths }],
      };
    });
  };

  const onDelete = index => {
    setInputs(prev => {
      const extraPayments = prev.extraPayments;
      return { ...prev, extraPayments: extraPayments.filter((_, i) => i !== index) };
    });
  };

  const onUpdateInt = (index, field, value0) => {
    const value = sanitizeNumericInput(String(value0));
    setInputs(prev => {
      const newInterest = prev.rateChanges.map((item, i) => (i === index ? { ...item, [field]: value } : item));
      return { ...prev, rateChanges: newInterest };
    });
  };

  const onAddInt = () => {
    setInputs(prev => {
      const current = prev.rateChanges || [];
      const lastTerm = current.length > 0 ? current[current.length - 1].term : 0;
      const newMonths = months.filter(m => m > lastTerm);
      const rate = String(+current[current.length - 1]?.rate + 1 || +prev.rate + 1);
      if (newMonths.length === 0) return prev;
      let indexMonth = current.length === 0 ? 1 : 0;
      return {
        ...prev,
        rateChanges: [...current, { term: newMonths[indexMonth], rate, months: newMonths }],
      };
    });
  };

  const onDeleteInt = index => {
    setInputs(prev => {
      const rateChanges = prev.rateChanges;
      return { ...prev, rateChanges: rateChanges.filter((_, i) => i !== index) };
    });
  };

  const onTabPress = tab => {
    setActiveTab(tab);
    if (tab === "adds" && adds.length === 0) onAdd();
    if (tab === "rates" && interestChanges.length === 0) onAddInt();
  };

  // Interest saved — both values are raw numbers, no string parsing needed
  // results.interest = original raw interest (number)
  // results2.interest2 = amended raw interest (number)
  const interestSaved =
    hasAdjustments && results?.interest != null && results2?.interest2 != null
      ? results.interest - results2.interest2
      : null;

  const interestSavedFormatted =
    interestSaved != null
      ? interestSaved.toLocaleString(currency?.locale || "en-ZA", {
          style: "currency",
          currency: currency?.code || "ZAR",
        })
      : "—";

  const monthsSaved = hasAdjustments && results2?.schedule ? Math.max(0, term - results2.schedule.length) : 0;

  // New instalment is only meaningful when there are rate changes —
  // extra payments shorten the term, not the instalment.
  // Find the last rate change and pull the instalment from that month in the schedule.
  const lastRateChange =
    interestChanges.length > 0
      ? interestChanges.reduce((last, c) => (+c.term > +last.term ? c : last), interestChanges[0])
      : null;

  const newInstalmentMonth = lastRateChange ? results2?.schedule?.find(row => row.term === +lastRateChange.term) : null;

  // Strip out extra payment and service fee so we show only the base instalment
  // driven by the rate change, not the total paid that month
  const newInstalmentValue =
    newInstalmentMonth != null
      ? newInstalmentMonth.instalment -
        (newInstalmentMonth.additionalPayment || 0) -
        (newInstalmentMonth.serviceFee || 0)
      : null;
  const newInstalmentFromMonth = lastRateChange?.term;

  const newInstalmentFormatted =
    newInstalmentValue != null
      ? newInstalmentValue.toLocaleString(currency?.locale || "en-ZA", {
          style: "currency",
          currency: currency?.code || "ZAR",
        })
      : null;

  return (
    <View>
      {/* {showBadge && (
        <View style={styles.badgeRow}>
          <View style={styles.badge}>
            <Text style={styles.badgeText}>A feature most calculators don't have</Text>
          </View>
        </View>
      )} */}

      <View style={styles.card}>
        <View style={styles.banner}>
          <Text style={styles.bannerText}>Model real-life changes to your loan</Text>
        </View>

        <View style={styles.header}>
          <Text style={styles.cardTitle}>Advanced settings</Text>
        </View>
        <Text style={styles.subtitle}>Add a lump-sum payment or rate hike, see the new total instantly</Text>

        {/* Segmented tab toggle */}
        <View style={styles.tabRow}>
          <Pressable
            onPress={() => onTabPress("adds")}
            style={[styles.tabPill, activeTab === "adds" ? styles.tabPillActive : styles.tabPillInactive]}
          >
            <Text style={[styles.tabPillText, activeTab === "adds" && styles.tabPillTextActive]}>
              Extra payments{adds.length > 0 ? ` (${adds.length})` : ""}
            </Text>
          </Pressable>
          <Pressable
            onPress={() => onTabPress("rates")}
            style={[styles.tabPill, activeTab === "rates" ? styles.tabPillActive : styles.tabPillInactive]}
          >
            <Text style={[styles.tabPillText, activeTab === "rates" && styles.tabPillTextActive]}>
              Rate changes{interestChanges.length > 0 ? ` (${interestChanges.length})` : ""}
            </Text>
          </Pressable>
        </View>

        {/* Extra payments rows */}
        {activeTab === "adds" && (
          <View style={styles.adjustmentArea}>
            {adds.map((add, i, arr) => (
              <AdjustmentRow
                key={i}
                add={add}
                length={arr.length}
                index={i}
                term={term}
                months={months}
                onAdd={onAdd}
                onUpdate={onUpdate}
                onDelete={onDelete}
              />
            ))}
            <Pressable onPress={onAdd} style={styles.addLink}>
              <Text style={styles.addLinkText}>+ Add another payment</Text>
            </Pressable>
          </View>
        )}

        {/* Rate changes rows */}
        {activeTab === "rates" && (
          <View style={styles.adjustmentArea}>
            {interestChanges.map((int, i, arr) => (
              <AdjustmentRowInt
                key={i}
                int={int}
                index={i}
                length={arr.length}
                term={term}
                months={months2}
                onAdd={onAddInt}
                onUpdate={onUpdateInt}
                onDelete={onDeleteInt}
              />
            ))}
            <Pressable onPress={onAddInt} style={styles.addLink}>
              <Text style={styles.addLinkText}>+ Add another rate change</Text>
            </Pressable>
          </View>
        )}

        {/* Live impact strip */}
        {hasAdjustments && (
          <View style={styles.impactStrip}>
            {/* New instalment — only shown when rate changes exist */}
            {newInstalmentFormatted != null && (
              <>
                <View style={styles.impactItem}>
                  <Text style={styles.impactLabel}>
                    New instalment *
                    {/* New instalment {newInstalmentFromMonth ? ` (from mo. ${newInstalmentFromMonth})` : ""} */}
                  </Text>
                  <Text style={styles.impactValue}>{newInstalmentFormatted}</Text>
                </View>
                <View style={styles.impactDivider} />
              </>
            )}

            <View style={styles.impactItem}>
              <Text style={styles.impactLabel}>Interest saved</Text>
              <Text
                style={[
                  styles.impactValue,
                  interestSaved != null ? (interestSaved >= 0 ? styles.impactPositive : styles.impactNegative) : null,
                ]}
              >
                {interestSavedFormatted}
              </Text>
            </View>
            <View style={styles.impactDivider} />
            <View style={styles.impactItem}>
              <Text style={styles.impactLabel}>Months saved</Text>
              <Text style={[styles.impactValue, monthsSaved >= 0 ? styles.impactPositive : styles.impactNegative]}>
                {monthsSaved}
              </Text>
            </View>
          </View>
        )}

        {hasAdjustments && newInstalmentFormatted != null && (
          <Text style={styles.instalmentNote}>
            {`* Instalment changed after interest rate change at month ${newInstalmentFromMonth}`}
          </Text>
        )}
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  badgeRow: {
    width: "90%",
    alignSelf: "center",
    alignItems: "center",
    marginBottom: 8,
  },

  badge: {
    backgroundColor: "#fac775",
    borderRadius: 12,
    paddingHorizontal: 12,
    paddingVertical: 5,
  },

  badgeText: {
    fontSize: 11,
    fontWeight: "500",
    color: "#412402",
  },

  card: {
    width: "90%",
    alignSelf: "center",
    backgroundColor: appColors.cardBg,
    borderRadius: 16,
    borderWidth: 1.5,
    borderColor: appColors.accent,
    overflow: "hidden",
    ...cardShadow(),
  },

  banner: {
    backgroundColor: appColors.accentLight,
    paddingHorizontal: 14,
    paddingVertical: 8,
  },

  bannerText: {
    fontSize: 11,
    fontWeight: "500",
    color: "#085041",
  },

  header: {
    paddingHorizontal: 14,
    paddingTop: 12,
  },

  cardTitle: {
    fontSize: 13,
    fontWeight: "500",
    color: appColors.textPrimary,
  },

  subtitle: {
    fontSize: 11,
    color: appColors.textSecondary,
    paddingHorizontal: 14,
    paddingTop: 2,
    paddingBottom: 10,
  },

  tabRow: {
    flexDirection: "row",
    gap: 8,
    paddingHorizontal: 14,
    paddingBottom: 12,
  },

  tabPill: {
    flex: 1,
    borderRadius: 10,
    paddingVertical: 9,
    alignItems: "center",
  },

  tabPillActive: {
    backgroundColor: appColors.primary,
  },

  tabPillInactive: {
    backgroundColor: appColors.inputBg,
  },

  tabPillText: {
    fontSize: 11,
    fontWeight: "500",
    color: appColors.textSecondary,
  },

  tabPillTextActive: {
    color: "#fff",
  },

  adjustmentArea: {
    paddingHorizontal: 14,
    paddingBottom: 12,
    gap: 6,
  },

  rowContainer: {
    flexDirection: "row",
    alignItems: "flex-end",
    backgroundColor: appColors.rowAlt,
    padding: 8,
    borderRadius: 10,
    gap: 6,
  },

  pickerWrapper: {
    flex: 1.5,
  },

  valueWrapper: {
    flex: 1,
  },

  inputLabel: {
    fontSize: 9,
    color: appColors.textMuted,
    marginBottom: 4,
    fontWeight: "500",
    textTransform: "uppercase",
    letterSpacing: 0.3,
  },

  pickerOutline: {
    borderWidth: 1,
    borderColor: appColors.border,
    borderRadius: 8,
    backgroundColor: appColors.cardBg,
    justifyContent: "center",
  },

  textInput: {
    height: 32,
    borderWidth: 1,
    borderColor: appColors.border,
    backgroundColor: appColors.cardBg,
    borderRadius: 8,
    paddingVertical: 2,
    paddingHorizontal: 10,
    fontSize: 12,
    color: appColors.primary,
    fontWeight: "500",
  },

  deleteBtn: {
    height: 32,
    width: 28,
    justifyContent: "center",
    alignItems: "center",
  },

  deleteText: {
    color: appColors.danger,
    fontWeight: "500",
    fontSize: 14,
  },

  addLink: {
    paddingVertical: 4,
  },

  addLinkText: {
    fontSize: 11,
    fontWeight: "500",
    color: appColors.primary,
  },

  impactStrip: {
    flexDirection: "row",
    backgroundColor: appColors.primary,
    marginHorizontal: 14,
    marginBottom: 14,
    borderRadius: 12,
    paddingVertical: 10,
    paddingHorizontal: 8,
  },

  impactItem: {
    flex: 1,
    alignItems: "center",
  },

  impactLabel: {
    fontSize: 9,
    color: appColors.primaryLight,
    marginBottom: 3,
    textAlign: "center",
  },

  impactValue: {
    fontSize: 13,
    fontWeight: "500",
    color: "#fff",
  },

  impactPositive: {
    color: "#5dcaa5",
  },

  impactNegative: {
    color: "#ff6b6b",
  },

  impactDivider: {
    width: 1,
    backgroundColor: "rgba(255,255,255,0.15)",
  },
  instalmentNote: {
    fontSize: 10,
    color: appColors.textMuted,
    paddingHorizontal: 14,
    paddingBottom: 10,
    fontStyle: "italic",
  },
});

export default AdvancedSettings;
